import { useState } from "react";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-faint/70 transition-colors duration-300 focus:border-amber focus:outline-none";

const labelCls =
  "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-faint";

const SPRINT_AREAS = [
  "Strategy",
  "Programs & Products",
  "Systems & Capacity",
  "AI & Automation",
  "Not sure yet",
];

const SPRINT_USEFUL = [
  "Clarity on the problem",
  "Help prioritizing what matters",
  "A practical roadmap",
  "Understanding what to build or change",
  "Identifying systems or AI opportunities",
  "Not sure yet",
];

const WORKSHOP_FORMATS = ["90-minute", "Half-day", "Not sure yet"];

export default function InquiryForm({ mode, testid }) {
  const isSprint = mode === "sprint";
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    main: "",
    area: "",
    useful: "",
    role: "",
    format: "",
    teamSize: "",
    extra: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    const needs = isSprint
      ? [
          `What they're trying to figure out:\n${form.main}`,
          form.area && `Where the challenge sits today: ${form.area}`,
          form.useful && `Most useful right now: ${form.useful}`,
          form.extra && `Anything else: ${form.extra}`,
        ]
          .filter(Boolean)
          .join("\n\n")
      : [
          `Workshop topic / challenge:\n${form.main}`,
          form.role && `Role: ${form.role}`,
          form.format && `Preferred format: ${form.format}`,
          form.teamSize && `Team size: ${form.teamSize}`,
          form.extra && `Additional context: ${form.extra}`,
        ]
          .filter(Boolean)
          .join("\n\n");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          organization: form.organization || null,
          audience: null,
          needs,
          source: isSprint ? "sprint" : "workshop",
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
    } catch {
      setStatus("error");
      setError(
        "Something went wrong sending your inquiry. Please try again, or email kpstrategyteam@gmail.com directly."
      );
    }
  };

  if (status === "success") {
    return (
      <div data-testid={`${testid}-success`} className="border border-amber/50 bg-amber-soft p-8 sm:p-10">
        <p className="font-serif text-2xl font-normal text-ink">
          Thanks — we&apos;ve received your inquiry.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-body">
          {isSprint
            ? "We'll be in touch to discuss whether a Strategy Sprint is the right next step."
            : "We'll be in touch to discuss the right workshop format for your team."}
        </p>
      </div>
    );
  }

  return (
    <form data-testid={testid} onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${testid}-name`} className={labelCls}>Name *</label>
          <input id={`${testid}-name`} data-testid={`${testid}-name`} required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
        </div>
        <div>
          <label htmlFor={`${testid}-email`} className={labelCls}>Email *</label>
          <input id={`${testid}-email`} data-testid={`${testid}-email`} required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
        </div>
      </div>

      <div>
        <label htmlFor={`${testid}-organization`} className={labelCls}>
          Organization / Company{isSprint && " (optional)"}
        </label>
        <input id={`${testid}-organization`} data-testid={`${testid}-organization`} required={!isSprint} value={form.organization} onChange={set("organization")} className={inputCls} placeholder="Company or organization" />
      </div>

      <div>
        <label htmlFor={`${testid}-main`} className={labelCls}>
          {isSprint ? "What are you trying to figure out? *" : "Workshop topic / challenge *"}
        </label>
        <textarea
          id={`${testid}-main`}
          data-testid={`${testid}-main`}
          required
          rows={4}
          value={form.main}
          onChange={set("main")}
          className={`${inputCls} resize-none`}
          placeholder={
            isSprint
              ? "Tell us what's happening, what feels unclear, or what you know needs to change."
              : "What are you trying to improve, solve or change?"
          }
        />
      </div>

      {isSprint && (
        <div>
          <label htmlFor={`${testid}-area`} className={labelCls}>
            Where does the challenge sit today? (optional)
          </label>
          <select
            id={`${testid}-area`}
            data-testid={`${testid}-area`}
            value={form.area}
            onChange={set("area")}
            className={`${inputCls} ${form.area ? "" : "text-faint/70"}`}
          >
            <option value="" disabled>
              Select an area
            </option>
            {SPRINT_AREAS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      )}

      {isSprint ? (
        <div>
          <label htmlFor={`${testid}-useful`} className={labelCls}>
            What would be most useful right now? (optional)
          </label>
          <select
            id={`${testid}-useful`}
            data-testid={`${testid}-useful`}
            value={form.useful}
            onChange={set("useful")}
            className={`${inputCls} ${form.useful ? "" : "text-faint/70"}`}
          >
            <option value="" disabled>
              Select one
            </option>
            {SPRINT_USEFUL.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <>
          <div>
            <label htmlFor={`${testid}-role`} className={labelCls}>
              Role (optional)
            </label>
            <input
              id={`${testid}-role`}
              data-testid={`${testid}-role`}
              value={form.role}
              onChange={set("role")}
              className={inputCls}
              placeholder="e.g. Executive Director, Operations Lead, Founder"
            />
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor={`${testid}-format`} className={labelCls}>
                Preferred format (optional)
              </label>
              <select
                id={`${testid}-format`}
                data-testid={`${testid}-format`}
                value={form.format}
                onChange={set("format")}
                className={`${inputCls} ${form.format ? "" : "text-faint/70"}`}
              >
                <option value="" disabled>
                  Select a format
                </option>
                {WORKSHOP_FORMATS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={`${testid}-team-size`} className={labelCls}>
                Team size (optional)
              </label>
              <input
                id={`${testid}-team-size`}
                data-testid={`${testid}-team-size`}
                value={form.teamSize}
                onChange={set("teamSize")}
                className={inputCls}
                placeholder="e.g. 6–10 participants"
              />
            </div>
          </div>
        </>
      )}

      <div>
        <label htmlFor={`${testid}-extra`} className={labelCls}>
          {isSprint ? "Anything else we should know? (optional)" : "Additional context (optional)"}
        </label>
        <textarea
          id={`${testid}-extra`}
          data-testid={`${testid}-extra`}
          rows={3}
          value={form.extra}
          onChange={set("extra")}
          className={`${inputCls} resize-none`}
        />
      </div>

      {status === "error" && (
        <p data-testid={`${testid}-error`} className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <button
        data-testid={`${testid}-submit`}
        type="submit"
        disabled={status === "sending"}
        className="group inline-flex items-center gap-2.5 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold tracking-wide text-paper transition-colors duration-300 hover:bg-amber disabled:opacity-60"
      >
        {status === "sending"
          ? "Sending…"
          : isSprint
            ? "Start the Conversation"
            : "Discuss the Workshop"}
        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
      </button>
    </form>
  );
}
